import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { Text } from '@adobe/react-spectrum';
import Avatar from 'app/components/SlideOutPanel/DACIContacts/Avatar';
import { getEmployeePhotoUrl } from './employeePhotoUrl';
import {
  calculateVerticalPlacement,
  getTooltipPortalTarget,
  POPOVER_PORTAL_Z_INDEX
} from './popoverPositioning';
import styles from './ApproverCell.module.scss';

export interface NameListPerson {
  uid: string | null;
  name: string;
}

// Grace period before the popover closes after the pointer leaves the
// trigger - same value/reasoning as ReleaseMomentDateCell's own
// HOVER_CLOSE_DELAY_MS: the popover is portaled to a separate part of the
// DOM with a visual gap to its trigger, so without a short delay the
// pointer crossing that gap closes it before it can ever be reached.
const POPOVER_CLOSE_DELAY_MS = 200;

// Keeps the popover from running off the right edge of the viewport. Its
// width is intrinsic (hugs the longest name in the list) rather than a
// known fixed constant like DateTooltip's 600px, so it's measured directly
// off the popover's own rendered element instead.
const POPOVER_VIEWPORT_MARGIN = 12;
const POPOVER_VERTICAL_GAP = 8;

interface NameListHoverPopoverProps {
  people: NameListPerson[];
  names: string;
  /** Applied to the wrapping trigger element - each caller supplies its
   * own layout class (e.g. ApproverCell's `.cell-content`,
   * NewFeedbackEditor's `.recipientsTrigger`) rather than this component
   * imposing one. */
  triggerClassName?: string;
  /**
   * Defaults to `POPOVER_PORTAL_Z_INDEX` (ApproverCell's original, unchanged
   * behavior - correct for a trigger living in normal page content, kept
   * below the real Moment slide-out's own stacking so that slide-out still
   * wins if both are ever visible at once). A caller whose OWN trigger
   * lives inside a slide-out (e.g. NewFeedbackEditor, inside the Feedback
   * slide-out's `.slideOutPanel`, z-index 100000) must pass a higher value
   * here - otherwise the portaled popover renders as a sibling of that
   * slide-out at a *lower* z-index and ends up hidden behind its own
   * container's opaque background, even though it's technically open.
   */
  zIndex?: number;
  /**
   * Whether the trigger keeps the plain browser-native `title` tooltip
   * alongside this popover - `true` by default (ApproverCell's existing,
   * unchanged behavior). A caller can set this to `false` where the native
   * tooltip would otherwise read as a confusing "second, plainer popover"
   * once this component's own hover card exists.
   */
  showNativeTitle?: boolean;
  renderBeforeAvatar?: (person: NameListPerson) => React.ReactNode;
  children: React.ReactNode;
}

/**
 * Generic hover popover listing people (avatar + name, one per row) -
 * originally built for the Approver(s) column's multi-approver case
 * (Figma's "MVP Hoverover" reference, node 18792:306232 and children, file
 * hBnevR1MeKGrm062iaGE1P) and extracted here so any other name-list
 * trigger (e.g. NewFeedbackEditor's "Feedback email recipients") reuses
 * the exact same popover instead of a second implementation. Reuses
 * ApproverCell.module.scss's own `.approver-popover`/
 * `.approver-popover-row`/`.approver-popover-name`/`.approver-avatar`
 * classes directly rather than a new stylesheet, and the same
 * portal-escape-the-clipped-table positioning technique
 * (calculateVerticalPlacement/getTooltipPortalTarget) every other popover
 * in this feature already uses. Unlike DateTooltip, this is a short-lived
 * read-only list, not something a user needs to keep open while
 * scrolling, so a scroll simply closes it instead of following the
 * trigger.
 *
 * `people` empty/missing is handled by rendering just `children` with no
 * wrapper, hover listeners, or popover at all - hovering an empty list
 * does nothing rather than opening an empty card.
 */
export function NameListHoverPopover({
  people,
  names,
  triggerClassName,
  zIndex = POPOVER_PORTAL_Z_INDEX,
  showNativeTitle = true,
  renderBeforeAvatar,
  children
}: NameListHoverPopoverProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsHovered(true);
  };

  const handleLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, POPOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!isHovered) return;
    if (triggerRef.current) {
      setAnchorRect(triggerRef.current.getBoundingClientRect());
    }

    const handleScroll = () => setIsHovered(false);
    window.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isHovered]);

  const verticalPlacement: 'top' | 'bottom' = anchorRect
    ? calculateVerticalPlacement(anchorRect)
    : 'bottom';

  // Writes the popover's position directly to the DOM node once it mounts
  // (ref callbacks fire synchronously during commit, before paint, so
  // there's no flash at the wrong position) - measured off the popover's
  // own offsetWidth/offsetHeight since, unlike DateTooltip, its size isn't
  // a fixed known constant.
  const setPopoverElementRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (!el || !anchorRect) return;
      const maxLeft =
        window.innerWidth - POPOVER_VIEWPORT_MARGIN - el.offsetWidth;
      const left = Math.min(
        anchorRect.left,
        Math.max(POPOVER_VIEWPORT_MARGIN, maxLeft)
      );
      const top =
        verticalPlacement === 'bottom'
          ? anchorRect.bottom + POPOVER_VERTICAL_GAP
          : anchorRect.top - POPOVER_VERTICAL_GAP - el.offsetHeight;
      el.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    },
    [anchorRect, verticalPlacement]
  );

  if (people.length === 0) {
    return <>{children}</>;
  }

  return (
    <div
      ref={triggerRef}
      className={triggerClassName}
      role="group"
      title={showNativeTitle ? names : undefined}
      aria-label={names}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {children}
      {isHovered &&
        anchorRect &&
        createPortal(
          <div
            ref={setPopoverElementRef}
            className={styles['approver-popover']}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex
            }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          >
            {people.map(person => (
              <div
                key={person.uid ?? person.name}
                className={styles['approver-popover-row']}
              >
                {renderBeforeAvatar?.(person)}
                <div className={styles['approver-avatar']}>
                  <Avatar
                    src={person.uid ? getEmployeePhotoUrl(person.uid) : ''}
                    alt={person.name}
                    name={person.name}
                    size={24}
                  />
                </div>
                <Text UNSAFE_className={styles['approver-popover-name']}>
                  {person.name}
                </Text>
              </div>
            ))}
          </div>,
          getTooltipPortalTarget()
        )}
    </div>
  );
}

export default NameListHoverPopover;
